import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { OptionCard } from 'components/OptionCard'
import {
  TERMS_LINKS_GROUP_VARIANT,
  TermsLinksGroup,
} from 'components/TermsLinksGroup'

import { useCohortInfo } from 'hooks/useCohortInfo'
import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useExperimentalFeatures } from 'hooks/useExperimentalFeatures'
import { useNextStep } from 'hooks/useNextStep'

import { TPageProps } from 'models/common.model'

import { CustomPageId, PageId } from 'page-constants'
import { Color, Language, OptionType, PageVersions } from 'root-constants'

import {
  COHORT_TO_VERSION_MAP,
  COMPACT_LABEL_COHORTS,
  GENDER_OPTIONS_BY_VERSION,
  PAGE_VERSIONS_WITH_REGULAR_SUBTITLE,
  PAGE_VERSIONS_WITH_SMALL_TEXT,
  PRINTABLE_COHORTS,
  VARIATION_COHORTS,
} from './constants'
import { getTitleKey } from './helpers'
import { StyledSelectGenderAyah as S } from './selectGenderAyah.styles'

export const SelectGenderAyah: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
  isFirstPage,
}) => {
  const { t } = useTranslation()
  const { cohortToUse } = useCohortInfo()

  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()

  const { isAyah39ScreenWordingTest } = useExperimentalFeatures()

  const [answer, setAnswer] = useState<string>('')

  const context =
    COHORT_TO_VERSION_MAP[cohortToUse] ?? COHORT_TO_VERSION_MAP.default
  const genderOptions = GENDER_OPTIONS_BY_VERSION[context]
  const isCompactLabel = COMPACT_LABEL_COHORTS.includes(cohortToUse)

  const shouldShowNote = context === PageVersions.V2
  const isSmallTitle = PAGE_VERSIONS_WITH_SMALL_TEXT.includes(context)
  const isSmallSubtitle = context === PageVersions.V6
  const isRegularSubtitle =
    PAGE_VERSIONS_WITH_REGULAR_SUBTITLE.includes(context)

  const shouldShowTerms = !PRINTABLE_COHORTS.includes(cohortToUse)
  const shouldRenderVariation = VARIATION_COHORTS.includes(cohortToUse)
  const shouldUsePaTitle = !shouldRenderVariation && isAyah39ScreenWordingTest
  const titleKey = getTitleKey({
    shouldRenderVariation,
    shouldUsePaTitle,
  })

  const handleContinue = useNextStep({
    pageId: CustomPageId.GENDER,
    question: t(titleKey, {
      lng: Language.EN,
      context,
    }),
    nextPagePath,
    isFirstPage: pageId === PageId.SELECT_GENDER_AYAH || isFirstPage,
  })

  const handleAnswerChange = (value: string) => {
    handleChange(value, handleContinue)
    setIsAnswersDisabled(true)
    setAnswer(value)
  }

  const renderTitleAndSubtitle = () => {
    if (shouldRenderVariation) {
      return (
        <>
          <S.TitleVariation>
            {t('onboarding.selectGenderAyah.titleVariation')}
          </S.TitleVariation>
          <S.SubtitleVariation>
            {t('onboarding.selectGenderAyah.subtitleVariation')}
          </S.SubtitleVariation>
        </>
      )
    }

    return (
      <>
        <S.Title $isSmall={isSmallTitle}>
          {shouldUsePaTitle ? (
            t(titleKey)
          ) : (
            <Trans i18nKey={titleKey} context={context} />
          )}
        </S.Title>
        <S.Subtitle $isRegular={isRegularSubtitle} $isSmall={isSmallSubtitle}>
          <Trans
            i18nKey="onboarding.selectGenderAyah.subtitle"
            context={context}
          />
        </S.Subtitle>
      </>
    )
  }

  return (
    <S.Container>
      {renderTitleAndSubtitle()}

      <S.CardsContainer>
        {genderOptions.map(({ value, text, imageUrl }) => (
          <OptionCard
            key={value}
            imageUrl={imageUrl}
            value={value}
            label={t(text)}
            checked={answer === value}
            disabled={isAnswersDisabled}
            $backgroundColor={Color.SKY_DARK}
            $isCompactLabel={isCompactLabel}
            name={pageId}
            type={OptionType.RADIO}
            onChange={handleAnswerChange}
          />
        ))}
      </S.CardsContainer>

      {shouldShowNote && (
        <S.Note>
          <Trans i18nKey="onboarding.selectGenderAyah.note" />
        </S.Note>
      )}

      {shouldShowTerms && (
        <>
          <TermsLinksGroup
            titleKey="commonComponents.termsGenderTitle"
            variant={TERMS_LINKS_GROUP_VARIANT.DARK}
          />

          <S.TermsNote>{t('commonComponents.termsNote')}</S.TermsNote>
        </>
      )}
    </S.Container>
  )
}
